import React, { useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { Formik, Field, FieldArray } from 'formik';
import { FiInfo, FiPlus, FiX } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { useToast } from '../../../../hooks/useToast';

import api from '../../../../services/api';

import {
  AddAnswerButton,
  AnswerRow,
  AnswersWrapper,
  FooterButton,
  FooterHint,
  ModalSubtitle,
  OrderBadge,
  RemoveAnswer,
  Step,
  StepHead,
  StepHint,
  TextInput,
  TypeCard,
  TypeGrid,
  TypeMarker,
} from './styles';

interface IAnswer {
  id: string;
  text: string;
}

interface IQuestion {
  id: string;
  text: string;
  type: string;
  answers: IAnswer[];
}

interface IResearch {
  id: string;
  title: string;
  status: string;
  description: string;
  start_date: Date;
  finish_date: Date;
  questions: IQuestion[];
}

interface IProps {
  show: boolean;
  handleShow: (value: boolean) => void;
  handleLoading: (value: boolean) => void;
  handleResearch: (research: IResearch) => void;
  researchId: string;
}

const letterFor = (index: number) => String.fromCharCode(65 + index);

const AddQuestionModal: React.FC<IProps> = ({
  show,
  handleShow,
  handleLoading,
  handleResearch,
  researchId: selectedResearch,
}) => {
  const { id: researchId } = useParams<{ id: string }>();
  const { addToast } = useToast();

  const handleClose = useCallback(() => {
    handleShow(false);
  }, [handleShow]);

  const handleAddQuestion = useCallback(
    async values => {
      try {
        handleLoading(true);
        const payload = {
          ...values,
          answers: values.answers.filter((answer: string) => answer.trim()),
        };
        await api.post('/research/question', payload);
        const updatedResearch = await api.get(`/research/${selectedResearch}`);
        handleResearch(updatedResearch.data);
      } catch (error) {
        addToast({
          title: 'Erro',
          body: 'Ocorreu algum erro ao tentar adicionar a questão. Tente novamente.',
        });
      } finally {
        handleClose();
        handleLoading(false);
      }
    },
    [addToast, handleClose, handleLoading, handleResearch, selectedResearch],
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      size="lg"
    >
      <Formik
        initialValues={{
          text: '',
          type: '',
          research_id: researchId,
          answers: ['', ''],
        }}
        onSubmit={handleAddQuestion}
      >
        {({ handleSubmit, values, handleChange, setFieldValue }) => {
          const filledAnswers = values.answers.filter(a => a.trim()).length;
          const canSubmit =
            values.text.trim().length > 0 &&
            !!values.type &&
            filledAnswers >= 2;

          return (
            <form onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <div>
                  <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>
                    Nova questão
                  </Modal.Title>
                  <ModalSubtitle>
                    Defina a pergunta, como o trabalhador irá respondê-la e as
                    opções disponíveis.
                  </ModalSubtitle>
                </div>
              </Modal.Header>

              <Modal.Body style={{ background: '#fafbfc', padding: 20 }}>
                {/* 1 · Pergunta */}
                <Step>
                  <StepHead>
                    <span className="badge">1</span>
                    <h4>Pergunta</h4>
                  </StepHead>
                  <StepHint>
                    Escreva de forma clara e objetiva. Ex.: “Como você avalia o
                    atendimento do seu sindicato?”
                  </StepHint>
                  <TextInput
                    type="text"
                    placeholder="Digite a pergunta"
                    onChange={handleChange('text')}
                    value={values.text}
                  />
                </Step>

                {/* 2 · Formato da resposta */}
                <Step>
                  <StepHead>
                    <span className="badge">2</span>
                    <h4>Formato da resposta</h4>
                  </StepHead>
                  <StepHint>
                    Escolha se o trabalhador poderá marcar apenas uma opção ou
                    várias.
                  </StepHint>
                  <TypeGrid>
                    <TypeCard
                      type="button"
                      $selected={values.type === 'radio'}
                      onClick={() => setFieldValue('type', 'radio')}
                    >
                      <span className="head">
                        <TypeMarker
                          $variant="radio"
                          $selected={values.type === 'radio'}
                        />
                        <strong>Única seleção</strong>
                      </span>
                      <small>O trabalhador escolhe apenas 1 opção.</small>
                    </TypeCard>

                    <TypeCard
                      type="button"
                      $selected={values.type === 'checkbox'}
                      onClick={() => setFieldValue('type', 'checkbox')}
                    >
                      <span className="head">
                        <TypeMarker
                          $variant="checkbox"
                          $selected={values.type === 'checkbox'}
                        />
                        <strong>Múltipla seleção</strong>
                      </span>
                      <small>O trabalhador pode marcar uma ou mais.</small>
                    </TypeCard>
                  </TypeGrid>
                </Step>

                {/* 3 · Opções */}
                <Step>
                  <StepHead>
                    <span className="badge">3</span>
                    <h4>Opções de resposta</h4>
                  </StepHead>
                  <StepHint>
                    Adicione pelo menos 2 opções. Prefira textos curtos e
                    diretos.
                  </StepHint>

                  <FieldArray name="answers">
                    {({ push, remove }) => (
                      <AnswersWrapper>
                        {values.answers.map((_, index) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <AnswerRow key={index}>
                            <OrderBadge>{letterFor(index)}</OrderBadge>
                            <Field
                              name={`answers.${index}`}
                              type="text"
                              placeholder={`Opção ${letterFor(index)}`}
                              autoComplete="off"
                            />
                            <RemoveAnswer
                              type="button"
                              aria-label="Remover opção"
                              disabled={values.answers.length <= 2}
                              onClick={() => remove(index)}
                            >
                              <FiX size={16} />
                            </RemoveAnswer>
                          </AnswerRow>
                        ))}

                        <AddAnswerButton type="button" onClick={() => push('')}>
                          <FiPlus size={14} />
                          Adicionar opção
                        </AddAnswerButton>
                      </AnswersWrapper>
                    )}
                  </FieldArray>
                </Step>

                <FooterHint>
                  <FiInfo size={13} />
                  <span>
                    Ao publicar a pesquisa, as questões não poderão mais ser
                    editadas. Revise antes de salvar.
                  </span>
                </FooterHint>
              </Modal.Body>

              <Modal.Footer>
                <FooterButton
                  type="button"
                  $variant="secondary"
                  onClick={handleClose}
                >
                  Cancelar
                </FooterButton>
                <FooterButton type="submit" disabled={!canSubmit}>
                  Salvar questão
                </FooterButton>
              </Modal.Footer>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AddQuestionModal;
